import { PieChart, Text, VStack } from '@talos/kyoko';
import { useMemo } from 'react';
import { renderToString } from 'react-dom/server';
import { useTheme } from 'styled-components';

import type { TotalCostRiskData } from '../../../types';
import { formatCostRiskBps } from '../../../utils';
import { TOTAL_COST_RISK_COLORS } from './totalCostRiskColors';

interface TotalCostRiskDonutChartProps {
  data: TotalCostRiskData;
}

export function TotalCostRiskDonutChart({ data }: TotalCostRiskDonutChartProps) {
  const theme = useTheme();

  const series = useMemo(() => {
    return [
      {
        type: 'pie' as const,
        name: 'Total Cost & Risk',
        data: [
          {
            name: 'Spread Cost',
            y: data.spreadCost,
            color: TOTAL_COST_RISK_COLORS.spreadCost,
          },
          {
            name: 'Temp. Cost',
            y: data.tempCost,
            color: TOTAL_COST_RISK_COLORS.tempCost,
          },
          {
            name: 'Risk',
            y: data.risk,
            color: TOTAL_COST_RISK_COLORS.risk,
          },
        ],
      },
    ];
  }, [data]);

  const options = useMemo(
    () => ({
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        height: 156,
        width: 156,
      },
      title: {
        useHTML: true,
        text: renderToString(
          <VStack gap={theme.spacing2} overflow="hidden" textAlign="center">
            <Text size={theme.fontSizeTiny} color={theme.colorTextDefault}>
              TOTAL
            </Text>
            <Text size={theme.fontSizeLarge} weight={theme.fontWeightMedium} color={theme.colorTextImportant}>
              {formatCostRiskBps(data.total)}
            </Text>
            <Text size={theme.fontSizeTiny} color={theme.colorTextDefault}>
              BPS
            </Text>
          </VStack>
        ),
        align: 'center' as const,
        floating: true,
        verticalAlign: 'middle' as const,
        style: {
          pointerEvents: 'none',
        },
      },
      plotOptions: {
        pie: {
          innerSize: '85%',
          dataLabels: {
            enabled: false,
          },
          borderWidth: 1,
          borderColor: theme.backgroundBody,
          borderRadius: 0,
        },
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    }),
    [data.total, theme]
  );

  return (
    <VStack alignItems="center" gap="spacing4" data-testid="total-cost-risk-donut-chart">
      <PieChart series={series} options={options} />
    </VStack>
  );
}
